import { memo } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import type { PurchaseReleaseListItem } from "src-next/modules/patient-supporting-documents";

interface PreviousReleasesSectionProps {
  items: PurchaseReleaseListItem[];
  loading: boolean;
  refreshing?: boolean;
  onRevoke: (batchId: string) => Promise<void>;
}

export const PreviousReleasesSection = memo(function PreviousReleasesSection({
  items,
  loading,
  refreshing = false,
  onRevoke,
}: PreviousReleasesSectionProps) {
  if (loading) {
    return (
      <Stack alignItems="center" py={3}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {refreshing && (
        <Alert severity="info" sx={{ py: 0.25 }}>
          Atualizando liberações…
        </Alert>
      )}
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" py={2}>
          Nenhuma liberação anterior para este paciente.
        </Typography>
      ) : (
        items.map((item) => {
          const expired =
            item.validUntil && dayjs(item.validUntil).isBefore(dayjs(), "day");
          return (
            <Stack
              key={item.batchId}
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
                {item.productNames.join(" · ")}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  {" "}
                  · em {dayjs(item.createdAt).format("DD/MM/YYYY")}
                  {item.validUntil
                    ? ` · válido até ${dayjs(item.validUntil).format("DD/MM/YYYY")}`
                    : ""}
                </Typography>
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={expired ? "Expirada" : item.statusLabel}
                color={expired ? "warning" : "success"}
              />
              {!expired && (
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  disabled={refreshing}
                  startIcon={<UndoOutlinedIcon />}
                  onClick={() => {
                    void (async () => {
                      try {
                        await onRevoke(item.batchId);
                        toast.success("Liberação revogada.");
                      } catch {
                        toast.error("Não foi possível revogar a liberação.");
                      }
                    })();
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Revogar
                </Button>
              )}
            </Stack>
          );
        })
      )}
    </Stack>
  );
});
